import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SellerDashboardLayout from '../../components/SellerDashboardLayout';
import { productFavoritesApi } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/Pagination/Pagination';
import './FavoritesProductPage.css';

const PLACEHOLDER_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E%3Cpath d='M75 80a20 20 0 1 1 40 0 20 20 0 0 1-40 0zm-25 60l30-40 20 25 15-20 30 35H50z' fill='%23ccc'/%3E%3C/svg%3E";

const resolveProductImage = (product) => {
  if (!product) return null;
  if (product.productImages && product.productImages.length > 0) {
    const url = product.productImages[0].url;
    if (url && !url.includes('default-product')) return url;
  }
  if (product.images) {
    try {
      const arr =
        typeof product.images === 'string' ? JSON.parse(product.images) : product.images;
      if (Array.isArray(arr) && arr[0]) return arr[0];
    } catch {}
  }
  if (product.image && !product.image.includes('default-product')) return product.image;
  return null;
};

const FavoritesProductPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const itemsPerPage = 20;

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const fetchFavorites = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await productFavoritesApi.getByUserId(user.id, {
        page: currentPage,
        limit: itemsPerPage,
      });
      setFavoriteProducts(response.favorites || []);
      setTotalPages(response.totalPages || 1);
    } catch (err) {
      console.error('Error fetching favorite products:', err);
      setError('Failed to load favourite products');
      setFavoriteProducts([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const handleRemoveFavorite = async (e, productId) => {
    e.stopPropagation();
    if (!user?.id) return;
    try {
      setRemovingId(productId);
      await productFavoritesApi.toggle(user.id, productId); // toggles it off
      setFavoriteProducts((prev) => prev.filter((f) => f.productId !== productId));
      showToast('Removed from favourites');
    } catch (err) {
      console.error('Error removing product favorite:', err);
      showToast('Could not remove. Please try again.', 'error');
    } finally {
      setRemovingId(null);
    }
  };

  const handleViewProduct = (favorite) => {
    const p = favorite.product;
    if (!p) return;
    navigate(`/product/${p.slug || p.id}`);
  };

  /* ─── Product Card ─── */
  const ProductFavCard = ({ favorite }) => {
    const product = favorite.product;
    const isRemoving = removingId === favorite.productId;
    if (!product) return null;

    const imgSrc = resolveProductImage(product) || PLACEHOLDER_IMG;
    const seller = product.seller || {};
    const priceText =
      product.priceMin && product.priceMax
        ? `${product.priceMin} – ${product.priceMax} ${product.unit || 'USD'}`
        : product.priceMin
        ? `From ${product.priceMin} ${product.unit || 'USD'}`
        : 'Price on request';

    return (
      <div
        className="fprod-card"
        onClick={() => handleViewProduct(favorite)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleViewProduct(favorite)}
      >
        {/* Image */}
        <div className="fprod-img-wrap">
          <img
            src={imgSrc}
            alt={product.name}
            className="fprod-img"
            onError={(e) => {
              e.target.src = PLACEHOLDER_IMG;
            }}
          />
          {/* Remove button */}
          <button
            className={`fprod-remove-btn ${isRemoving ? 'removing' : ''}`}
            onClick={(e) => handleRemoveFavorite(e, favorite.productId)}
            disabled={isRemoving}
            title="Remove from favourites"
            aria-label="Remove from favourites"
          >
            {/* Filled bookmark = in favourites */}
            <svg width="14" height="19" viewBox="0 0 14 19" fill="none">
              <path
                d="M12 0C12.3978 0 12.7792 0.158149 13.0605 0.439453C13.3419 0.720758 13.5 1.10218 13.5 1.5V18C13.4998 18.1337 13.4634 18.2647 13.3955 18.3799C13.3276 18.495 13.2302 18.5905 13.1133 18.6553C12.9964 18.72 12.864 18.7524 12.7305 18.7487C12.5969 18.745 12.4664 18.7055 12.3535 18.6347L6.75 14.9297L1.14648 18.6347C1.03357 18.7055 0.90306 18.745 0.769531 18.7487C0.636003 18.7524 0.503552 18.72 0.38665 18.6553C0.269749 18.5905 0.172363 18.495 0.104492 18.3799C0.0366211 18.2647 0.000366211 18.1337 0 18V1.5C0 1.10218 0.158149 0.720758 0.439453 0.439453C0.720758 0.158149 1.10218 0 1.5 0H12Z"
                fill="#1F396C"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="fprod-body">
          <h3 className="fprod-name">{product.name}</h3>

          {/* Seller row */}
          {seller.name && (
            <div className="fprod-seller-row">
              {seller.logo && (
                <img src={seller.logo} alt={seller.name} className="fprod-seller-logo" />
              )}
              <span className="fprod-seller-name">{seller.name}</span>
              {seller.countryFlag && (
                <img src={seller.countryFlag} alt={seller.country} className="fprod-flag" />
              )}
            </div>
          )}

          {/* Price */}
          <div className="fprod-price">{priceText}</div>

          {/* Category */}
          {product.category?.name && (
            <div className="fprod-category">{product.category.name}</div>
          )}

          {/* MOQ */}
          {product.moq && (
            <div className="fprod-moq">MOQ: {product.moq}</div>
          )}
        </div>
      </div>
    );
  };

  /* ─── Empty State ─── */
  const EmptyState = () => (
    <div className="fprod-empty">
      <div className="fprod-empty-icon">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
          <path
            d="M22 0H11.5C11.1022 0 10.7206 0.158035 10.4393 0.43934C10.158 0.720644 10 1.10218 10 1.5V18C10.0001 18.1338 10.036 18.2652 10.1039 18.3805C10.1719 18.4958 10.2695 18.5908 10.3866 18.6557C10.5037 18.7206 10.636 18.7529 10.7698 18.7494C10.9036 18.7458 11.034 18.7066 11.1475 18.6356L16.75 15.1341L22.3534 18.6356C22.4669 18.7063 22.5972 18.7454 22.7309 18.7488C22.8646 18.7522 22.9967 18.7198 23.1136 18.655C23.2306 18.5902 23.3281 18.4953 23.396 18.3801C23.4639 18.2649 23.4998 18.1337 23.5 18V1.5C23.5 1.10218 23.342 0.720644 23.0607 0.43934C22.7794 0.158035 22.3978 0 22 0ZM22 16.6472L17.1466 13.6144C17.0274 13.5399 16.8896 13.5004 16.7491 13.5004C16.6085 13.5004 16.4708 13.5399 16.3516 13.6144L11.5 16.6472V1.5H22V16.6472Z"
            fill="#E4E4E7"
          />
        </svg>
      </div>
      <h3 className="fprod-empty-title">No Favourite Products Yet</h3>
      <p className="fprod-empty-desc">
        Browse products and click the bookmark icon to save them here for quick access.
      </p>
      <button className="fprod-explore-btn" onClick={() => navigate('/products')}>
        Explore Products
      </button>
    </div>
  );

  /* ─── Loading Skeleton ─── */
  const LoadingSkeleton = () => (
    <div className="fprod-grid">
      {Array(8)
        .fill(null)
        .map((_, i) => (
          <div key={i} className="fprod-card fprod-skeleton">
            <div className="fprod-img-wrap fprod-sk-img" />
            <div className="fprod-body">
              <div className="fprod-sk-line fprod-sk-title" />
              <div className="fprod-sk-line fprod-sk-short" />
              <div className="fprod-sk-line fprod-sk-price" />
            </div>
          </div>
        ))}
    </div>
  );

  return (
    <SellerDashboardLayout>
      <div className="fprod-page">
        {/* Header */}
        <div className="fprod-header">
          <div>
            <h1 className="fprod-title">Favourite Products</h1>
            <p className="fprod-subtitle">
              Your saved products — find and revisit them anytime.
            </p>
          </div>
        </div>

        {/* Tabs linking to both favourite pages */}
        <div className="fprod-tabs">
          <button
            className="fprod-tab"
            onClick={() => navigate('/favorites-supplier')}
          >
            Saved Suppliers
          </button>
          <button className="fprod-tab fprod-tab--active">
            Saved Products
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="fprod-error">
            <span>{error}</span>
            <button onClick={fetchFavorites}>Try Again</button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <LoadingSkeleton />
        ) : favoriteProducts.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="fprod-grid">
              {favoriteProducts.map((fav) => (
                <ProductFavCard key={fav.id || fav.productId} favorite={fav} />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fav-toast fav-toast--${toast.type}`}>{toast.msg}</div>
      )}
    </SellerDashboardLayout>
  );
};

export default FavoritesProductPage;
